import { useState } from "react";
import { Grid } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { BackButton } from "layouts/views/components/Button";
import { retailBarcodeValidation } from "layouts/views/common/validation";

import { detailRender, detailValue, detailValidation } from "./sku-retail-barcode-details-schema";

import SKURetailBarcodeDetail from "./components/SKURetailBarcodeDetail";

import { getSKURetailBarcode, createSKURetailBarcode } from "services/data.internal.service";

import { ReadonlyText } from "ui-components/CustomInput";
import { FormTitle } from "ui-components/Form";
import { Layout } from "ui-components/Layout";
import { Success, Error_ } from "ui-components/Alert";
import {
  StyledDataGrid,
  DelAction,
  CancelAction,
} from "ui-components/data-grids/StripedCheckboxDataGrid";
import { ActivateAction } from "ui-components/data-grids/StripedCheckboxDataGrid";
import { DeactivateAction } from "ui-components/data-grids/StripedCheckboxDataGrid";

function AddSKURetailBarcode() {
  const navigate = useNavigate();
  const location = useLocation();
  const { skuCode, customerCode, vendorCode } = location?.state ?? {};

  const [details, setDetails] = useState([]);
  const [alert, setAlert] = useState(null);

  const onSuccess = (data) => setAlert(<Success data={data} onChange={setAlert} />);

  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const [formValue, setFormValue] = useState(null);

  const detailProps = { detailRender, detailValue, detailValidation, details, setDetails };

  const { isLoading } = useQuery({
    queryKey: ["retail-barcode"],
    queryFn: () => getSKURetailBarcode({ skuCode, customerCode, vendorCode }),
    enabled: Boolean(skuCode) && Boolean(customerCode) && Boolean(vendorCode),
    onSuccess: (data) => {
      const { details: barcode_details, ...retail } = data.data.data;

      setFormValue({ ...retail, vendorCode });

      setDetails(barcode_details.map((detail, i) => ({ ...detail, no: i + 1, newItem: false })));
    },
    onError,
  });

  const editRetailBarcodeMutation = useMutation(createSKURetailBarcode, {
    onSuccess,
    onError,
  });

  const onSubmit = () => {
    window.scrollTo(0, 0);
    const { customerCode, skuCode, vendorCode } = formValue;

    const req = {
      customerCode,
      skuCode,
      vendorCode,
      category:formValue.category,
      details: details.map((detail) => {
        const { no, ...retail } = detail;
        return retail;
      }),
    };

    editRetailBarcodeMutation.mutate(req, {
      onSuccess: () => {
        navigate("/setup/sku/pack-type/add", { state: { skuCode } });
      },
    });
  };

  return (
    <Layout>
      {alert}
      <FormTitle title="SKU Retail Barcode" />
      <MDBox mt={1.625}>
        <HeaderForm barcode={formValue} />{" "}
      </MDBox>
      <MDBox mt={5}>
        <DetailForm {...detailProps} />
      </MDBox>
      <MDBox mt={2} width="100%" display="flex" justifyContent="center">
        <MDButton
          sx={{ marginLeft: "1.5rem" }}
          type="button"
          onClick={onSubmit}
          variant="gradient"
          color="info"
          disabled={details.length === 0}
        >
          Submit
        </MDButton>
        <BackButton url={-1} />
      </MDBox>
    </Layout>
  );
}

const DetailForm = ({ detailRender, detailValue, detailValidation, details, setDetails }) => {
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      type: "actions",
      getActions: (params) => {
        const { row } = params;
        const CellItem = row.newItem ? (
          <DelAction onClick={() => handleDelete(row)} />
        ) : (
          <DelAction onClick={() => handleCancel(row,false)} />
        );
        return [CellItem];
      },
      width: 80,
    },
    {
      field: "no",
      headerName: "No",
      sortable: false,
      width: 80,
      align: "right",
    },
    {
      field: "retailBarcode",
      headerName: "Retail Barcode",
      sortable: false,
      width: 300,
      flex: 1,
    },
    {
      field: "uomType",
      headerName: "UOM Type",
      sortable: false,
      width: 300,
      flex: 1,
    },
  ];

  const [formValue, setFormValue] = useState(null);
  const [alert, setAlert] = useState(null);

  const form = useForm({
    defaultValues: { ...detailValue },
    values: formValue || { ...detailValue },
    resolver: yupResolver(detailValidation),
  });

  const {
    control,
    formState: { errors, isDirty },
    watch,
    reset,
    handleSubmit,
  } = form;

  const [newItem, active] = watch(["newItem", "active"]);

  const detailProps = { detailRender, control, errors, newItem, active };

  const onSubmit = (data) => {
    const isSKUValid = retailBarcodeValidation(details, data);
    if (isSKUValid) {
      setAlert(<Error_ content="Duplicate Retail Barcode" onChange={setAlert} />);

      return;
    }

    if (formValue === null) {
      setDetails([...details, { no: details.length + 1, ...data, active: true, newItem: true }]);
      reset();
    } else {
      setDetails(
        details.map((x) => {
          if (x.no === data.no) return data;
          return x;
        })
      );
      setFormValue(null);
    }
  };

  const handleDelete = (values) => {
    if (values.newItem === false) return;

    const filter = details.filter((x) => x.no !== values.no);
    const newDetail = filter.map((x, i) => {
      return { ...x, no: i + 1 };
    });

    setDetails([...newDetail]);
    setFormValue(null);
  };

  const handleCancel = (values, active) => {
    if (values.newItem === true) return;

    const newDetails = details.map((x) => {
      if (x.no === values.no) {
        x.active = active;
      }
      return x;
    });
    setDetails(newDetails);
    setFormValue(null);
  };

  const getRowClassName = (params) => {
    if (params.row.newItem === true) return "";
    return `status--${params.row.active ? "O" : "C"}`;
  };

  return (
    <>
      {alert}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <SKURetailBarcodeDetail {...detailProps} />
        <MDBox mt={2} width="100%" display="flex" justifyContent="center">
          {formValue === null ? (
            <MDButton type="submit" variant="gradient" color="info">
              Add
            </MDButton>
          ) : (
            <>
              <MDButton type="submit" variant="gradient" color="success" disabled={!isDirty}>
                Update
              </MDButton>
              <MDButton
                sx={{ marginLeft: "1.5rem" }}
                type="button"
                onClick={() => setFormValue(null)}
                variant="gradient"
                color="info"
              >
                Back
              </MDButton>
            </>
          )}
        </MDBox>
      </form>

      <MDBox>
        <MDTypography pb={1} color="info" variant="h5">
          Datagrid Display
        </MDTypography>
        <StyledDataGrid
          onRowClick={(v) => setFormValue(v.row)}
          columns={columns}
          rows={details.map((detail, i) => ({ id: i + 1, ...detail }))}
          getRowClassName={getRowClassName}
        />
      </MDBox>
    </>
  );
};

const HeaderForm = ({ barcode }) => {
  return (
    <>
      <Grid sx={{ mb: 1.5 }} container spacing={3}>
        <Grid item xs={3}>
          <ReadonlyText
            field={{
              label: "SKU Code",
              name: "skuCode",
              value: barcode?.skuCode,
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <ReadonlyText
            field={{ label: "Customer Code", name: "customerCode", value: barcode?.customerCode }}
          />
        </Grid>
        <Grid item xs={3}>
          <ReadonlyText
            field={{
              label: "Descriptions 1",
              name: "descriptions1",
              value: barcode?.descriptions1,
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <ReadonlyText
            field={{ label: "Vendor Code", name: "vendorCode", value: barcode?.vendorCode }}
          />
        </Grid>
      </Grid>
      <Grid sx={{ mb: 1.5 }} container spacing={3}>
        <Grid item xs={3}>
          <ReadonlyText
            field={{
              label: "Category",
              name: "category",
              value: barcode?.category,
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <ReadonlyText
            field={{ label: "SKU Group", name: "skuGroup", value: barcode?.skuGroup }}
          />
        </Grid>
        <Grid item xs={3}>
          <ReadonlyText
            field={{
              label: "SKU Sub Group",
              name: "skuSubGroup",
              value: barcode?.skuSubGroup,
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <ReadonlyText field={{ label: "Size", name: "size", value: barcode?.size }} />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={3}>
          <ReadonlyText
            field={{
              label: "Color",
              name: "color",
              value: barcode?.color,
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <ReadonlyText
            field={{
              label: "Style",
              name: "style",
              value: barcode?.style,
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default AddSKURetailBarcode;
